import React from 'react';
import Icon from 'components/AppIcon';

const LifestyleImpactSection = ({ data = {}, onChange, errors = [] }) => {
  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleMultiSelectChange = (field, value, checked) => {
    const currentValues = data[field] || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter(v => v !== value);
    onChange({ [field]: newValues });
  };

  const sleepChangesOptions = [
    { value: 'no-change', label: 'No significant change' },
    { value: 'sleeping-more', label: 'Sleeping more than usual' },
    { value: 'sleeping-less', label: 'Sleeping less than usual' },
    { value: 'difficulty-falling-asleep', label: 'Difficulty falling asleep' },
    { value: 'frequent-waking', label: 'Waking up frequently during the night' },
    { value: 'poor-quality', label: 'Poor sleep quality' }
  ];

  const eatingChangesOptions = [
    { value: 'no-change', label: 'No significant change' },
    { value: 'eating-more', label: 'Eating more than usual' },
    { value: 'eating-less', label: 'Eating less than usual' },
    { value: 'unhealthy-foods', label: 'Eating more unhealthy foods' },
    { value: 'irregular-meals', label: 'Irregular meal times' },
    { value: 'emotional-eating', label: 'Emotional eating' }
  ];

  const exerciseChangesOptions = [
    { value: 'no-change', label: 'No significant change' },
    { value: 'exercising-more', label: 'Exercising more than before' },
    { value: 'exercising-less', label: 'Exercising less than before' },
    { value: 'stopped-exercising', label: 'Stopped exercising completely' },
    { value: 'changed-routine', label: 'Changed exercise routine' }
  ];

  const screenTimeChanges = [
    { value: 'no-change', label: 'No significant change' },
    { value: 'increased-work', label: 'Increased due to work/study' },
    { value: 'increased-entertainment', label: 'Increased for entertainment' },
    { value: 'increased-social', label: 'Increased for social connection' },
    { value: 'decreased', label: 'Decreased screen time' }
  ];

  const workChanges = [
    { value: 'no-change', label: 'No change in work situation' },
    { value: 'working-from-home', label: 'Started working from home' },
    { value: 'job-loss', label: 'Lost job or reduced hours' },
    { value: 'increased-workload', label: 'Increased workload or stress' },
    { value: 'career-change', label: 'Changed career or job' },
    { value: 'retired', label: 'Retired or stopped working' }
  ];

  const socialChanges = [
    { value: 'no-change', label: 'No significant change' },
    { value: 'less-social', label: 'Less social interaction' },
    { value: 'isolated', label: 'Feeling isolated or lonely' },
    { value: 'virtual-socializing', label: 'More virtual socializing' },
    { value: 'family-time', label: 'More time with family/household' },
    { value: 'lost-connections', label: 'Lost touch with friends/colleagues' }
  ];

  return (
    <div className="space-y-8">
      {/* Sleep Changes */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          How have your sleep patterns changed since the pandemic? *
        </label>
        <div className="space-y-2">
          {sleepChangesOptions.map((option) => (
            <label
              key={option.value}
              className={`
                relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${data.sleepChanges === option.value
                  ? 'border-primary bg-primary-50 text-primary-600' :'border-border bg-surface hover:border-border-focus'
                }
              `}
            >
              <input
                type="radio"
                name="sleepChanges"
                value={option.value}
                checked={data.sleepChanges === option.value}
                onChange={(e) => handleInputChange('sleepChanges', e.target.value)}
                className="sr-only"
              />
              <div className={`
                w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                ${data.sleepChanges === option.value
                  ? 'border-primary bg-primary' :'border-border'
                }
              `}>
                {data.sleepChanges === option.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="text-sm font-medium">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Eating Habits */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          How have your eating habits changed?
        </label>
        <div className="space-y-2">
          {eatingChangesOptions.map((option) => (
            <label
              key={option.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.eatingChanges || []).includes(option.value)}
                onChange={(e) => handleMultiSelectChange('eatingChanges', option.value, e.target.checked)}
                className="w-4 h-4 text-secondary border-border rounded focus:ring-secondary focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Exercise Changes */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          How has your exercise routine changed?
        </label>
        <div className="space-y-2">
          {exerciseChangesOptions.map((option) => (
            <label
              key={option.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.exerciseChanges || []).includes(option.value)}
                onChange={(e) => handleMultiSelectChange('exerciseChanges', option.value, e.target.checked)}
                className="w-4 h-4 text-accent border-border rounded focus:ring-accent focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Screen Time */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          How has your screen time changed?
        </label>
        <div className="space-y-2">
          {screenTimeChanges.map((option) => (
            <label
              key={option.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.screenTimeChanges || []).includes(option.value)}
                onChange={(e) => handleMultiSelectChange('screenTimeChanges', option.value, e.target.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Work Changes */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          How has your work situation changed?
        </label>
        <div className="space-y-2">
          {workChanges.map((option) => (
            <label
              key={option.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.workChanges || []).includes(option.value)}
                onChange={(e) => handleMultiSelectChange('workChanges', option.value, e.target.checked)}
                className="w-4 h-4 text-secondary border-border rounded focus:ring-secondary focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Social Changes */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          How have your social connections changed?
        </label>
        <div className="space-y-2">
          {socialChanges.map((option) => (
            <label
              key={option.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.socialChanges || []).includes(option.value)}
                onChange={(e) => handleMultiSelectChange('socialChanges', option.value, e.target.checked)}
                className="w-4 h-4 text-accent border-border rounded focus:ring-accent focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Lifestyle Impact Summary */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Overall Lifestyle Impact (Optional)
        </label>
        <textarea
          value={data.lifestyleImpactSummary || ''}
          onChange={(e) => handleInputChange('lifestyleImpactSummary', e.target.value)}
          placeholder="Describe how the pandemic has overall impacted your daily life and routines..."
          rows={4}
          className="form-input resize-none"
        />
        <p className="mt-1 text-xs text-text-secondary">
          Share any additional details about how your lifestyle has changed
        </p>
      </div>

      {/* Lifestyle Tips */}
      <div className="bg-secondary-50 border border-secondary-100 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={20} color="#059669" className="mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-secondary-600 mb-2">
              Lifestyle Wellness Tips
            </h3>
            <ul className="text-sm text-secondary-600 space-y-1">
              <li>• Maintain regular sleep schedules even when working from home</li>
              <li>• Take regular breaks from screens throughout the day</li>
              <li>• Try to maintain some form of physical activity daily</li>
              <li>• Stay connected with friends and family through various means</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifestyleImpactSection;